export type Member = {
  MemberId: string;
  Email: string;
  Name: string;
  Gender?: string | null;
  BirthDate?: string | null;
  Phone?: string | null;
  AddressCity?: string | null;
  AddressDistrict?: string | null;
  AddressPostalCode?: string | null;
  AddressLine?: string | null;
  CreatedAt?: string | null;
};

type ApiResponse<T> = T & {
  ok: boolean;
  message?: string;
  errors?: Record<string, string>;
};

const csrfKey = "fulling_member_csrf";

export function getMemberCsrfToken() {
  return sessionStorage.getItem(csrfKey) || "";
}

function setMemberCsrfToken(token?: string) {
  if (token) {
    sessionStorage.setItem(csrfKey, token);
  }
}

export function clearMemberCsrfToken() {
  sessionStorage.removeItem(csrfKey);
}

export class MemberApiError extends Error {
  errors?: Record<string, string>;
  needsVerification?: boolean;
  expired?: boolean;

  constructor(message: string, errors?: Record<string, string>, needsVerification?: boolean, expired?: boolean) {
    super(message);
    this.errors = errors;
    this.needsVerification = needsVerification;
    this.expired = expired;
  }
}

type RequestOptions = {
  body?: string;
  headers?: Record<string, string>;
  method?: string;
};

async function request<T>(path: string, options: RequestOptions = {}, isCsrfRetry = false): Promise<ApiResponse<T>> {
  const response = await fetch(path, {
    credentials: "include",
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(getMemberCsrfToken() ? { "X-CSRF-Token": getMemberCsrfToken() } : {}),
      ...(options.headers || {}),
    },
  });
  const data = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (data && "CsrfToken" in data) {
    setMemberCsrfToken(String((data as { CsrfToken?: string }).CsrfToken || ""));
  } else if (response.status === 403 && !isCsrfRetry && options.method && options.method !== "GET") {
    // 後端對 CSRF 失敗一律回傳 403（跟「尚未登入」的 401 是分開的），代表快取在 sessionStorage 的
    // Token 已經跟目前 Session 對不上（例如 Session 過期、後端重啟、開了很久沒動作等）。
    // 過去這裡什麼都不做，導致同一個失效的 Token 會被無限重送，使用者只能不斷看到
    // 「驗證逾時，請重新整理頁面後再試」，而重新整理其實不會清掉 sessionStorage，問題不會自己好。
    // 這裡改成：清掉舊 Token、跟後端要一個新的，再自動重送一次原本的請求，最多重試一次，
    // 讓使用者的同一次點擊就能成功，不需要自己手動重整或等 sessionStorage 過期。
    clearMemberCsrfToken();
    await fetchMemberCsrf();
    return request<T>(path, options, true);
  }
  if (!response.ok || data.ok === false) {
    throw new MemberApiError(
      data.message || "操作失敗，請稍後再試。",
      data.errors,
      (data as { NeedsVerification?: boolean }).NeedsVerification,
      (data as { Expired?: boolean }).Expired
    );
  }
  return data;
}

export async function fetchMemberCsrf() {
  const data = await request<{ CsrfToken: string }>("/api/member/csrf");
  setMemberCsrfToken(data.CsrfToken);
  return data.CsrfToken;
}

async function ensureCsrf() {
  if (!getMemberCsrfToken()) {
    await fetchMemberCsrf();
  }
}

export type RegisterPayload = {
  Name: string;
  Email: string;
  Password: string;
  Gender?: "Male" | "Female";
  Phone?: string;
  BirthYear?: string;
  BirthMonth?: string;
  BirthDay?: string;
  AddressCity?: string;
  AddressDistrict?: string;
  AddressPostalCode?: string;
  AddressLine?: string;
  Captcha: string;
  AgreeTerms: boolean;
};

export async function registerMember(payload: RegisterPayload) {
  await ensureCsrf();
  return request<Record<string, never>>("/api/member/register", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getCaptchaImageUrl() {
  return `/api/member/captcha?t=${Date.now()}`;
}

export async function forgotMemberPassword(email: string) {
  await ensureCsrf();
  return request<Record<string, never>>("/api/member/forgot-password", {
    method: "POST",
    body: JSON.stringify({ Email: email }),
  });
}

export async function resetMemberPassword(token: string, newPassword: string) {
  return request<{ Member: Member }>("/api/member/reset-password", {
    method: "POST",
    body: JSON.stringify({ Token: token, NewPassword: newPassword }),
  });
}

export async function verifyMemberEmail(token: string) {
  return request<{ Member?: Member; AlreadyVerified?: boolean; Expired?: boolean }>(
    `/api/member/verify-email?Token=${encodeURIComponent(token)}`
  );
}

export async function resendVerificationEmail(email: string) {
  await ensureCsrf();
  return request<Record<string, never>>("/api/member/resend-verification", {
    method: "POST",
    body: JSON.stringify({ Email: email }),
  });
}

export async function loginMember(email: string, password: string) {
  await ensureCsrf();
  return request<{ Member: Member }>("/api/member/login", {
    method: "POST",
    body: JSON.stringify({ Email: email, Password: password }),
  });
}

export async function getCurrentMember() {
  return request<{ Member: Member | null }>("/api/member/me");
}

export async function logoutMember() {
  const result = await request<Record<string, never>>("/api/member/logout", { method: "POST" });
  clearMemberCsrfToken();
  return result;
}

export type UpdateMemberProfilePayload = {
  Name: string;
  Phone?: string;
  Gender?: "Male" | "Female" | "";
  BirthYear?: string;
  BirthMonth?: string;
  BirthDay?: string;
  AddressCity?: string;
  AddressDistrict?: string;
  AddressPostalCode?: string;
  AddressLine?: string;
};

export async function updateMemberProfile(payload: UpdateMemberProfilePayload) {
  return request<{ Member: Member }>("/api/member/profile", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function changeMemberPassword(payload: { CurrentPassword: string; NewPassword: string }) {
  return request<Record<string, never>>("/api/member/change-password", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type NewsletterStatus = {
  Subscribed: boolean;
  SubscribedAt?: string | null;
  UnsubscribedAt?: string | null;
};

export async function getNewsletterStatus() {
  return request<{ Newsletter: NewsletterStatus }>("/api/member/newsletter-status");
}

export async function subscribeNewsletter() {
  return request<{ Newsletter: NewsletterStatus }>("/api/member/newsletter-subscribe", { method: "POST" });
}

export async function unsubscribeNewsletterAsMember() {
  return request<{ Newsletter: NewsletterStatus }>("/api/member/newsletter-unsubscribe", { method: "POST" });
}
